import { IST_TIMEZONE } from '@constants/date';
import { PaymentOrderTermType } from '@types';
import { formatISODate } from '@utils/date';

export const formatTermDate = (date: string) =>
  formatISODate({ date, format: 'DD MMM YYYY', timeZone: IST_TIMEZONE });

const formatISTDay = (date: string | Date) =>
  formatISODate({ date, format: 'yyyy-mm-dd', timeZone: IST_TIMEZONE });

export const paymentStatus = (row: PaymentOrderTermType) =>
  row.payment_status?.toLowerCase() || '';

const termStatus = (row: PaymentOrderTermType) =>
  row.status?.toLowerCase() || '';

export const isPaidComplete = (row: PaymentOrderTermType) =>
  paymentStatus(row) === 'success';

export const isTermDisabled = (row: PaymentOrderTermType) =>
  paymentStatus(row) === 'disabled';

const isProcessing = (row: PaymentOrderTermType) =>
  paymentStatus(row) === 'processing';

const isTermInactive = (
  row: PaymentOrderTermType,
  paymentLinkNotGenerated: boolean
) => !paymentLinkNotGenerated && termStatus(row) === 'inactive';

export const isPaymentLinkNotGenerated = (row: PaymentOrderTermType) => {
  if (paymentStatus(row) !== 'payment_order_pending' || !row.due_date) {
    return false;
  }
  return formatISTDay(row.due_date) < formatISTDay(new Date());
};

export const nonZeroAmount = (v: unknown) => Number(v ?? 0) !== 0;

const disabledTermTooltipText = (row: PaymentOrderTermType) => {
  const raw = (row.disabled_at ?? '').trim();
  if (!raw) return 'Payment link disabled';
  return `Payment link disabled on ${formatTermDate(raw)}`;
};

type TermStatusDisplay = {
  timelineIcon: string;
  tooltipIcon: string;
  tooltipText: string;
};

export const getTermStatusDisplay = (
  row: PaymentOrderTermType,
  paymentLinkNotGenerated: boolean
): TermStatusDisplay => {
  if (isPaidComplete(row)) {
    return {
      timelineIcon: 'rounded-green-tick-with-bg',
      tooltipIcon: 'check-circle-broken-green',
      tooltipText: 'Payment success'
    };
  }
  if (paymentLinkNotGenerated) {
    return {
      timelineIcon: 'rounded-unlink-with-bg',
      tooltipIcon: 'unlink-red',
      tooltipText: 'Payment link not generated'
    };
  }
  if (isTermInactive(row, paymentLinkNotGenerated)) {
    return {
      timelineIcon: 'rounded-scheduled-timer-with-bg',
      tooltipIcon: 'clock-check',
      tooltipText: 'The term is inactive'
    };
  }
  if (isTermDisabled(row)) {
    return {
      timelineIcon: 'disabled-with-bg',
      tooltipIcon: 'slash-circle',
      tooltipText: disabledTermTooltipText(row)
    };
  }
  if (isProcessing(row)) {
    return {
      timelineIcon: 'rounded-orange-tick-with-bg',
      tooltipIcon: 'check-circle-broken-orange',
      tooltipText: 'Pending payment'
    };
  }
  return {
    timelineIcon: 'rounded-scheduled-timer-with-bg',
    tooltipIcon: 'clock-check',
    tooltipText: 'Payment scheduled'
  };
};

export const termDisplayName = (
  name: string | undefined,
  index: number,
  termNumber?: number
) => {
  const raw = (name ?? '').trim();
  if (raw) return raw;
  const displayTermNumber = termNumber ?? index + 1;
  return `Installment ${displayTermNumber}`;
};

export const segmentConnectorClass = (row: PaymentOrderTermType) =>
  isPaidComplete(row) ? 'border-cofeeGreen7' : 'border-cofeeNeutralInactive';
